import type { ReactNode } from "react"
import { useMutation } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { Redirect } from "wouter"
import { ServerCog, Trash2, CalendarX, Loader2 } from "lucide-react"
import { api, ApiRequestError } from "@/lib/api"
import { useAdminAuthStore } from "@/store/adminAuthStore"
import { primaryBtnClass } from "@/lib/styleClasses"
import type { UbyportAutoSubmitRun, UbyportCleanupRun, AutoCancelRun } from "@fohet/caravan-booker-shared"

// Platform super-admin (role === "admin") operational tools. Cross-camp
// actions that don't belong on any single camp's admin screens — manual
// triggers for the scheduled background jobs. The API gate (requireRole) is
// the real guard; this page is only reachable via the admin-only nav item,
// but we still redirect non-admins who guess the URL.

interface JobCardProps {
    icon: ReactNode
    title: string
    description: string
    runLabel: string
    runningLabel: string
    isPending: boolean
    onRun: () => void
}

const JobCard = ({ icon, title, description, runLabel, runningLabel, isPending, onRun }: JobCardProps) => (
    <div className="bg-white rounded-[28px] border border-slate-100 shadow-xl p-6 space-y-4">
        <div className="flex items-start gap-3">
            <span className="size-10 rounded-2xl bg-slate-100 flex items-center justify-center shrink-0">{icon}</span>
            <div className="min-w-0">
                <h2 className="text-lg font-black text-slate-900">{title}</h2>
                <p className="text-slate-500 font-bold text-sm mt-0.5">{description}</p>
            </div>
        </div>
        <button type="button" onClick={onRun} disabled={isPending} className={primaryBtnClass}>
            {isPending && <Loader2 className="size-4 animate-spin" />}
            {isPending ? runningLabel : runLabel}
        </button>
    </div>
)

export const AdminPlatformPage = () => {
    const { t } = useTranslation()
    const user = useAdminAuthStore((s) => s.user)

    const failToast = (e: unknown, key: string) =>
        toast.error(e instanceof ApiRequestError ? e.message : t(key))

    const ubyportSubmit = useMutation({
        mutationFn: () => api.post<UbyportAutoSubmitRun>("/admin/jobs/ubyport-auto-submit", {}),
        onSuccess: (r) => {
            toast.success(
                t("admin.platform.ubyportSubmit.toastDone", {
                    sent: r.sentGuests,
                    errors: r.errorGuests,
                    reservations: r.processedReservations,
                }),
            )
            if (r.transportAborted) toast.warning(t("admin.platform.ubyportSubmit.toastAborted"))
        },
        onError: (e) => failToast(e, "admin.platform.ubyportSubmit.toastFailed"),
    })

    const autoCancel = useMutation({
        mutationFn: () => api.post<AutoCancelRun>("/admin/jobs/auto-cancel", {}),
        onSuccess: (r) => {
            toast.success(
                t("admin.platform.autoCancel.toastDone", {
                    cancelled: r.cancelled,
                    reminders: r.reminder1Sent + r.reminder2Sent,
                    holds: r.holdsDeleted,
                }),
            )
        },
        onError: (e) => failToast(e, "admin.platform.autoCancel.toastFailed"),
    })

    const ubyportCleanup = useMutation({
        mutationFn: () => api.post<UbyportCleanupRun>("/admin/jobs/ubyport-cleanup", {}),
        onSuccess: (r) => {
            toast.success(
                t("admin.platform.ubyportCleanup.toastDone", {
                    submissions: r.submissionsDeleted,
                    files: r.s3ObjectsDeleted,
                }),
            )
        },
        onError: (e) => failToast(e, "admin.platform.ubyportCleanup.toastFailed"),
    })

    if (user && user.role !== "admin") return <Redirect to="~/admin" />

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            <header>
                <h1 className="text-3xl font-black tracking-tight text-slate-900">{t("admin.platform.title")}</h1>
                <p className="text-slate-500 font-bold mt-1">{t("admin.platform.subtitle")}</p>
            </header>

            <div className="space-y-4">
                <JobCard
                    icon={<ServerCog className="size-5 text-slate-600" />}
                    title={t("admin.platform.ubyportSubmit.title")}
                    description={t("admin.platform.ubyportSubmit.description")}
                    runLabel={t("admin.platform.run")}
                    runningLabel={t("admin.platform.running")}
                    isPending={ubyportSubmit.isPending}
                    onRun={() => ubyportSubmit.mutate()}
                />
                <JobCard
                    icon={<CalendarX className="size-5 text-slate-600" />}
                    title={t("admin.platform.autoCancel.title")}
                    description={t("admin.platform.autoCancel.description")}
                    runLabel={t("admin.platform.run")}
                    runningLabel={t("admin.platform.running")}
                    isPending={autoCancel.isPending}
                    onRun={() => autoCancel.mutate()}
                />
                <JobCard
                    icon={<Trash2 className="size-5 text-slate-600" />}
                    title={t("admin.platform.ubyportCleanup.title")}
                    description={t("admin.platform.ubyportCleanup.description")}
                    runLabel={t("admin.platform.run")}
                    runningLabel={t("admin.platform.running")}
                    isPending={ubyportCleanup.isPending}
                    onRun={() => ubyportCleanup.mutate()}
                />
            </div>
        </div>
    )
}
